import { formatEther } from 'viem'
import { apyToApr } from '../../../../utils/parsing'

/**
 * Raw MarketsInfo struct returned by TakaraMarketState.getActiveMarketsInfo().
 *
 * On-chain values (tvl, totalSupply, totalBorrows) are already multiplied
 * by the oracle price and divided by underlying decimals, so they represent
 * price-scaled amounts (roughly USD in oracle-price units at 1e18 scale).
 */
export interface TakaraMarketsInfo {
  tvl: bigint
  ltv: bigint
  exchangeRateCurrent: bigint
  reserveFactorMantissa: bigint
  totalSupply: bigint
  isListed: boolean
  totalBorrows: bigint
  supplyRatePerBlock: bigint
  borrowRatePerBlock: bigint
  blocksPerYear: bigint
  timestampsPerYear: bigint
  token: string // cToken address
  underlying: string // underlying token address
  symbol: string // cToken symbol
  underlyingSymbol: string
}

function toBigInt(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') return BigInt(value)
  if (typeof value === 'string') return BigInt(value)
  if (value && typeof value === 'object') {
    const v = value as any
    if (typeof v._hex === 'string') return BigInt(v._hex)
    if (typeof v.toString === 'function') return BigInt(v.toString())
  }
  throw new Error(`Cannot convert value to bigint: ${String(value)}`)
}

function field<T = unknown>(src: any, key: string, index: number): T {
  if (src == null) throw new Error('Source is null/undefined')
  if (key in src) return src[key]
  if (Array.isArray(src)) return src[index]
  return (src as any)[key]
}

/**
 * Computes APY from a per-second rate and timestampsPerYear.
 * rate is 1e18-scaled; timestampsPerYear is typically 31_536_000.
 */
function computeApy(rateRaw: bigint, timestampsPerYear: bigint): number {
  const rate = Number(formatEther(rateRaw))
  const tpy = Number(timestampsPerYear)
  if (tpy === 0 || rate === 0) return 0
  // daily rate = rate * (timestampsPerYear / 365)
  const dailyRate = (rate * tpy) / 365
  return (Math.pow(dailyRate + 1, 365) - 1) * 100
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value !== 0n
  if (typeof value === 'number') return value !== 0
  return Boolean(value)
}

export function parseTakaraMarketsInfo(input: any): TakaraMarketsInfo {
  return {
    tvl: toBigInt(field(input, 'tvl', 0)),
    ltv: toBigInt(field(input, 'ltv', 1)),
    exchangeRateCurrent: toBigInt(field(input, 'exchangeRateCurrent', 2)),
    reserveFactorMantissa: toBigInt(field(input, 'reserveFactorMantissa', 3)),
    totalSupply: toBigInt(field(input, 'totalSupply', 4)),
    isListed: toBool(field(input, 'isListed', 5)),
    totalBorrows: toBigInt(field(input, 'totalBorrows', 6)),
    supplyRatePerBlock: toBigInt(field(input, 'supplyRatePerBlock', 7)),
    borrowRatePerBlock: toBigInt(field(input, 'borrowRatePerBlock', 8)),
    blocksPerYear: toBigInt(field(input, 'blocksPerYear', 9)),
    timestampsPerYear: toBigInt(field(input, 'timestampsPerYear', 10)),
    token: String(field(input, 'token', 11)),
    underlying: String(field(input, 'underlying', 12)),
    symbol: String(field(input, 'symbol', 13)),
    underlyingSymbol: String(field(input, 'underlyingSymbol', 14)),
  }
}

/**
 * Returns deposit APR and borrow APR (percentages) for one TAKARA market.
 */
export function getTakaraRates(info: TakaraMarketsInfo) {
  const supplyApy = computeApy(info.supplyRatePerBlock, info.timestampsPerYear)
  const borrowApy = computeApy(info.borrowRatePerBlock, info.timestampsPerYear)
  return {
    depositRate: apyToApr(supplyApy / 100) * 100,
    variableBorrowRate: apyToApr(borrowApy / 100) * 100,
  }
}
